/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file MetabolicControlDiabetesView.jsx
 * @author Fernando Ruano
 * @description Metabolic Control Diabetes View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

// Commons
import styleGuideColors from 'commons/styleGuideColors';

// Components
import DashboardCard from 'components/DashboardCard';
import DashboardDoughnut from 'components/Charts/DashboardDoughnut';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * Metabolic Control Diabetes View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function MetabolicControlDiabetesView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getMetabolicControlDiabetes } = dashboardStaffActions;

  const classifications = [
    {
      label: '>=9',
      percentLabel: '≥9%',
      color: styleGuideColors.alertError
    },
    {
      label: '>=8 - 9',
      percentLabel: '≥8% - 9%',
      color: styleGuideColors.graphYellow
    },
    {
      label: '>=7 - 8',
      percentLabel: '≥7% - 8%',
      color: styleGuideColors.baxterBrandCyan
    },
    {
      label: '>=6 - 7',
      percentLabel: '≥6% - 7%',
      color: styleGuideColors.baxterBrandTeal
    },
    {
      label: '<6',
      percentLabel: '<6%',
      color: styleGuideColors.alrertDark
    }
  ];

  const onGetData = (_, resp) => {
    let data = [];
    let total = 0;
    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });
      classifications.forEach(({ label, color, percentLabel }) => {
        const respElement = resp.data.find(
          ({ clasificacion }) => clasificacion === label
        );

        if (respElement) {
          const value = (respElement.cantidad * 100) / total;
          data = [
            ...data,
            {
              value: useTruncateAndFormat(value, 2),
              label: percentLabel,
              key: label,
              color,
              cantidad: respElement.cantidad
            }
          ];
        }
      });
      setChartData(data);
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    getMetabolicControlDiabetes({ clinicId }, onGetData);
  }, [clinicId, translate]);

  const handleDetailClick = (index) => {
    const { key: classification, cantidad: totalItems } = chartData[index];
    history.push(
      `/main/dashboard-detail/metabolic-control-diabetes/${classification}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F21" loading={!chartData}>
      <DashboardDoughnut
        title={translate('dashboardStaff.metabolicDiabetesTitle')}
        description={translate('dashboardStaff.metabolicDiabetesDesc')}
        dataValues={chartData}
        onDetailClick={handleDetailClick}
      />
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
MetabolicControlDiabetesView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getMetabolicControlDiabetes: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default MetabolicControlDiabetesView;
